<template>
  <p class="label">{{ label }}{{ required ? '*' : '' }} :</p>
  <div class="cursor-container">
    <div class="input-cursor" :class="{ error: errors && errors.length > 0 }">
      <section>
        <div>
          <input
            type="range"
            :id="id"
            :min="0"
            :max="10"
            v-model="inputValue"
            @input="emits('update:modelValue', inputValue)"
          />
        </div>
        <p>{{ inputValue.toString() === '' ? '5' : inputValue }} / 10</p>
      </section>
      <div v-if="errors && errors.length > 0" class="input-common__error">
        <AlertCircle :size="16" />
        <p>{{ errors[0].$message }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ErrorObject } from '@vuelidate/core'
import { AlertCircle } from 'lucide-vue-next'
import { watch, computed, ref, onMounted } from 'vue'

const props = defineProps<{
  id: string
  range: number
  label: string
  modelValue?: number
  required?: boolean
  errors?: ErrorObject[]
  value: number
}>()

const input = computed({
  get: () => {
    return props.modelValue
  },
  set: (v?: number): void => {
    emits('update:modelValue', v)
  }
})

const inputValue = ref<number>(props.value)

const emits = defineEmits<{
  (e: 'update:modelValue', value?: number): void
}>()

watch(inputValue, (newValue) => {
  emits('update:modelValue', newValue)
})
</script>

<style scoped lang="scss">
/********** Range Input Styles **********/

input[type='range'] {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  cursor: pointer;
  width: 15rem;
}

input[type='range']:focus {
  outline: none;
}

input[type='range']::-webkit-slider-runnable-track {
  background-color: var(--vc-dark);
  border-radius: 0.5rem;
  height: 0.5rem;
}

input[type='range']::-webkit-slider-thumb {
  appearance: none;
  margin-top: -12px;

  background-color: var(--vcrgb-danger);
  height: 2rem;
  width: 1rem;
}

input[type='range']:focus::-webkit-slider-thumb {
  border: 1px solid var(--vc-primary);
  outline: 3px solid var(--vc-primary);
  outline-offset: 0.125rem;
}

input[type='range']::-moz-range-track {
  background-color: var(--vc-dark);
  border-radius: 0.5rem;
  height: 0.5rem;
}

input[type='range']::-moz-range-thumb {
  border: none;
  border-radius: 0;

  background-color: var(--vc-primary);
  height: 2rem;
  width: 1rem;
}

input[type='range']::-webkit-slider-thumb,
input[type='range']::-webkit-slider-thumb:active {
  background-color: var(--vc-primary);
}

.label {
  margin-bottom: 15px;
  font-weight: 500;
}

.input-cursor {
  display: flex;
  flex-direction: column-reverse;
  justify-content: center;
  align-items: center;
  gap: 5px;
  color: rgba(var(--vcrgb-dark), 0.75);

  &__error {
    color: var(--vc-danger);
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 5px;
  }
}

.cursor-container {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.icon {
  position: absolute;
  z-index: 10;
  left: 0;
  width: 20px;
  pointer-events: none;
}

.input-cursor > section {
  display: flex;
  align-items: center;
  gap: 30px;
}

.input-cursor > section > div {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: default;
}

.error > input {
  border: 2px solid rgba(var(--vcrgb-danger), 0.5);
}
</style>
